import { Injectable, OnDestroy } from "@angular/core";
import { Observable, of, shareReplay, finalize, tap, from, catchError, switchMap } from "rxjs";
import { HttpParams } from "@angular/common/http";

export interface CacheConfig {
  prefix: string;
  maxAge: number;
  cleanupInterval: number;
}

interface CacheEntry<T> {
  id: string;
  prefix: string;
  timestamp: number;
  data: T;
}

@Injectable({
  providedIn: "root",
})
export class HttpCacheService implements OnDestroy {
  private pendingCalls: Map<string, Map<string, Observable<any>>> = new Map();

  private cleanupTimers: Map<string, number> = new Map();

  private configs: Map<string, CacheConfig> = new Map();

  private dbName = 'HttpCacheDB';
  private dbVersion = 1;
  private storeName = 'cache';
  private db: IDBDatabase | null = null;
  private dbReady: Promise<IDBDatabase>;

  constructor() {
    this.dbReady = this.initDB();
  }

  private initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Errore apertura IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB inizializzato con successo');
        resolve(request.result);
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
          
          objectStore.createIndex('prefix', 'prefix', { unique: false });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('prefix_timestamp', ['prefix', 'timestamp'], { unique: false });
          
          console.log('Object store cache creato');
        }
      };
    });
  }

  registerCache(config: CacheConfig): void {
    if (this.configs.has(config.prefix)) {
      console.warn(`Cache con prefisso "${config.prefix}" già registrata`);
      return;
    }

    this.configs.set(config.prefix, config);
    this.pendingCalls.set(config.prefix, new Map());

    this.startCacheCleanup(config);
  }

  private hashKey(key: any | HttpParams): string {
    let str: string;
    
    if (key instanceof HttpParams) {
      const paramsObj: { [key: string]: string } = {};
      key.keys().forEach(k => {
        paramsObj[k] = key.get(k) || '';
      });
      str = JSON.stringify(paramsObj, Object.keys(paramsObj).sort());
    } else {
      str = JSON.stringify(key);
    }
    
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) + str.charCodeAt(i);
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  private async getCachedAsync<T>(prefix: string, key: any | HttpParams): Promise<T | null> {
    const config = this.configs.get(prefix);
    if (!config) {
      console.error(`Cache con prefisso "${prefix}" non registrata`);
      return null;
    }

    try {
      await this.dbReady;
      if (!this.db) {
        console.error('Database non inizializzato');
        return null;
      }

      const cacheKey = prefix + this.hashKey(key);
      
      return new Promise<T | null>((resolve) => {
        const transaction = this.db!.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.get(cacheKey);

        request.onsuccess = () => {
          const entry: CacheEntry<T> | undefined = request.result;
          
          if (entry && entry.data) {
            const age = Date.now() - entry.timestamp;
            if (age > config.maxAge) {
              this.deleteCacheEntry(cacheKey);
              resolve(null);
            } else {
              resolve(entry.data);
            }
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error(`Errore nel recupero della cache (${prefix}):`, request.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.error(`Errore nel recupero della cache (${prefix}):`, error);
      return null;
    }
  }

  private async setCachedAsync<T>(prefix: string, key: any | HttpParams, data: T): Promise<void> {
    const config = this.configs.get(prefix);
    if (!config) {
      console.error(`Cache con prefisso "${prefix}" non registrata`);
      return;
    }

    try {
      await this.dbReady;
      if (!this.db) {
        console.error('Database non inizializzato');
        return;
      }

      const cacheKey = prefix + this.hashKey(key);
      const cacheEntry: CacheEntry<T> = {
        id: cacheKey,
        prefix: prefix,
        timestamp: Date.now(),
        data: data
      };

      return new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.put(cacheEntry);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error(`Errore nel salvataggio della cache (${prefix}):`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`Errore nel salvataggio della cache (${prefix}):`, error);
    }
  }

  private async deleteCacheEntry(id: string): Promise<void> {
    try {
      await this.dbReady;
      if (!this.db) return;

      return new Promise<void>((resolve) => {
        const transaction = this.db!.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.delete(id);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Errore eliminazione voce cache:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error('Errore eliminazione voce cache:', error);
    }
  }

  cachedRequest<T>(
    prefix: string,
    key: any | HttpParams,
    httpCall: () => Observable<T>
  ): Observable<T> {
    const config = this.configs.get(prefix);
    if (!config) {
      console.error(`Cache con prefisso "${prefix}" non registrata. Esecuzione chiamata senza cache.`);
      return httpCall();
    }

    const cacheKey = this.hashKey(key);
    const pendingCallsMap = this.pendingCalls.get(prefix);

    const pendingCall = pendingCallsMap?.get(cacheKey);
    if (pendingCall) {
      console.log(`Chiamata già in corso, attesa... (${prefix})`);
      return pendingCall as Observable<T>;
    }

    const cache$ = from(this.getCachedAsync<T>(prefix, key)).pipe(
      switchMap((cachedData) => {
        if (cachedData) {
          console.log(`Dati recuperati dalla cache IndexedDB (${prefix})`);
          return of(cachedData);
        }

        console.log(`Nuova chiamata HTTP (${prefix})`);
        
        return httpCall().pipe(
          tap((data) => {
            this.setCachedAsync(prefix, key, data).then(() => {
              console.log(`Dati salvati in IndexedDB (${prefix})`);
            });
          })
        );
      }),
      shareReplay(1),
      finalize(() => {
        pendingCallsMap?.delete(cacheKey);
      })
    );

    pendingCallsMap?.set(cacheKey, cache$);

    return cache$;
  }

  private startCacheCleanup(config: CacheConfig): void {
    this.cleanupExpiredCache(config.prefix);

    const timer = window.setInterval(() => {
      this.cleanupExpiredCache(config.prefix);
    }, config.cleanupInterval);
    
    this.cleanupTimers.set(config.prefix, timer);
  }

  private async cleanupExpiredCache(prefix: string): Promise<void> {
    const config = this.configs.get(prefix);
    if (!config) {
      return;
    }

    try {
      await this.dbReady;
      if (!this.db) {
        return;
      }

      const now = Date.now();
      const maxAge = config.maxAge;

      return new Promise<void>((resolve) => {
        const transaction = this.db!.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const index = objectStore.index('prefix');
        const request = index.openCursor(IDBKeyRange.only(prefix));
        
        let deletedCount = 0;

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          
          if (cursor) {
            const entry: CacheEntry<any> = cursor.value;
            const age = now - entry.timestamp;

            if (age > maxAge) {
              cursor.delete();
              deletedCount++;
            }
            
            cursor.continue();
          } else {
            if (deletedCount > 0) {
              console.log(`Cache IndexedDB ${prefix} pulita: ${deletedCount} voci rimosse`);
            }
            resolve();
          }
        };

        request.onerror = () => {
          console.error(`Errore durante la pulizia della cache ${prefix}:`, request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error(`Errore durante la pulizia della cache ${prefix}:`, error);
    }
  }

  async clearCache(prefix: string): Promise<void> {
    try {
      await this.dbReady;
      if (!this.db) {
        return;
      }

      return new Promise<void>((resolve) => {
        const transaction = this.db!.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const index = objectStore.index('prefix');
        const request = index.openCursor(IDBKeyRange.only(prefix));
        
        let deletedCount = 0;

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            console.log(`Cache IndexedDB ${prefix} svuotata: ${deletedCount} voci rimosse`);
            resolve();
          }
        };

        request.onerror = () => {
          console.error(`Errore durante lo svuotamento della cache ${prefix}:`, request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error(`Errore durante lo svuotamento della cache ${prefix}:`, error);
    }
  }

  async clearAllCaches(): Promise<void> {
    const promises = Array.from(this.configs.keys()).map(prefix => this.clearCache(prefix));
    await Promise.all(promises);
  }

  ngOnDestroy(): void {
    this.cleanupTimers.forEach((timer) => {
      clearInterval(timer);
    });
    this.cleanupTimers.clear();
    
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

